import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Radio,
  Divider,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { SPECIMEN_TYPE_URL_MAP } from 'src/constants/specimen_types';
import { SpecimenTypeInfo } from 'src/hooks/use_ordering_specimen_types';
import { OrderingPathway, SpecimenType } from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

interface OrderTypeAndPathwaySelectorProps {
  isOpen: boolean;
  specimenTypes: SpecimenTypeInfo[];
  isCSOrSalesUser: boolean;
  onSelect: (
    specimenType: SpecimenType,
    orderingPathway: OrderingPathway,
  ) => void;
  onCancel: () => void;
}

// URL-friendly mapping for ordering pathways
const ORDERING_PATHWAY_URL_MAP: Record<OrderingPathway, string> = {
  [OrderingPathway.DefaultNonReflexive]: 'default_non_reflexive',
  [OrderingPathway.CustomTrfNonReflexive]: 'custom_trf_non_reflexive',
  [OrderingPathway.StickerTrfNonReflexive]: 'sticker_trf_non_reflexive',
  [OrderingPathway.StickerTrfReflexive]: 'sticker_trf_reflexive',
  [OrderingPathway.OtherFormReflexive]: 'other_form_reflexive',
};

const URL_TO_ORDERING_PATHWAY_MAP: Record<string, OrderingPathway> = {
  default_non_reflexive: OrderingPathway.DefaultNonReflexive,
  custom_trf_non_reflexive: OrderingPathway.CustomTrfNonReflexive,
  sticker_trf_non_reflexive: OrderingPathway.StickerTrfNonReflexive,
  sticker_trf_reflexive: OrderingPathway.StickerTrfReflexive,
  other_form_reflexive: OrderingPathway.OtherFormReflexive,
};

// Display names for each pathway
export const ORDERING_PATHWAY_DISPLAY_NAMES: Record<OrderingPathway, string> = {
  [OrderingPathway.DefaultNonReflexive]: 'Default (non-reflexive)',
  [OrderingPathway.CustomTrfNonReflexive]: 'Custom TRF (non-reflexive)',
  [OrderingPathway.StickerTrfNonReflexive]: 'Sticker TRF (non-reflexive)',
  [OrderingPathway.StickerTrfReflexive]: 'Sticker TRF (reflexive)',
  [OrderingPathway.OtherFormReflexive]: 'Other form (reflexive)',
};

const OptionContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:first-of-type': {
    marginTop: 0,
  },
  '&:last-of-type': {
    marginBottom: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  alignItems: 'center',
  '& .MuiRadio-root': {
    paddingRight: theme.spacing(1),
  },
  '& .MuiFormControlLabel-label': {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.text.primary,
  },
}));

const OptionDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.subtitle2.fontSize,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
  '&:first-of-type': {
    marginTop: 0,
  },
}));

export const OrderTypeAndPathwaySelector: React.FC<
  OrderTypeAndPathwaySelectorProps
> = ({ isOpen, specimenTypes, isCSOrSalesUser, onSelect, onCancel }) => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter to only show enabled types
  const enabledTypes = specimenTypes.filter((type) => type.enabled);

  // Get selected values from URL params
  const selectedSpecimenTypeUrlValue = searchParams.get('specimen_type') || '';
  const selectedPathwayUrlValue = searchParams.get('ordering_pathway') || '';

  const specimenTypeFromUrl =
    SpecimenTypeUtils.URL_TO_SPECIMEN_TYPE_MAP[selectedSpecimenTypeUrlValue] ||
    null;
  const pathwayFromUrl =
    URL_TO_ORDERING_PATHWAY_MAP[selectedPathwayUrlValue] || null;

  // Only allow selection if the specimen type is both valid AND enabled
  const selectedSpecimenType =
    enabledTypes.length === 1
      ? enabledTypes[0].type
      : specimenTypeFromUrl &&
          enabledTypes.some((type) => type.type === specimenTypeFromUrl)
        ? specimenTypeFromUrl
        : null;

  // Default to DefaultNonReflexive if nothing is selected for pathway
  const selectedPathway = pathwayFromUrl || OrderingPathway.DefaultNonReflexive;

  // All available pathways
  const pathways: OrderingPathway[] = [
    OrderingPathway.DefaultNonReflexive,
    OrderingPathway.CustomTrfNonReflexive,
    OrderingPathway.StickerTrfNonReflexive,
    OrderingPathway.StickerTrfReflexive,
    OrderingPathway.OtherFormReflexive,
  ];

  // Set default pathway in URL if not present (for CS/Sales users)
  useEffect(() => {
    if (isCSOrSalesUser && !selectedPathwayUrlValue) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(
            'ordering_pathway',
            ORDERING_PATHWAY_URL_MAP[OrderingPathway.DefaultNonReflexive],
          );
          return newParams;
        },
        { replace: true },
      );
    }
  }, [isCSOrSalesUser, selectedPathwayUrlValue, setSearchParams]);

  // Clear invalid specimen type from URL if it's not enabled (replace to avoid extra history entry)
  useEffect(() => {
    if (specimenTypeFromUrl && !selectedSpecimenType) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('specimen_type');
          return newParams;
        },
        { replace: true },
      );
    }
  }, [specimenTypeFromUrl, selectedSpecimenType, setSearchParams]);

  if (enabledTypes.length === 0) {
    return (
      <Dialog
        open={isOpen}
        onClose={onCancel}
        maxWidth='sm'
        fullWidth
        data-testid='new-order-type-selector-dialog'
      >
        <DialogTitle sx={{ textAlign: 'left' }}>{'Order Type'}</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            No test types are available for your current region.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={onCancel}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const handleContinue = () => {
    // Always have a selected specimen type (either from selection or auto-selected if only one)
    const finalSpecimenType = selectedSpecimenType || enabledTypes[0]?.type;
    if (finalSpecimenType) {
      onSelect(finalSpecimenType, selectedPathway);
    }
  };

  const handleSpecimenTypeClick = (specimenType: SpecimenType) => {
    const urlValue = SPECIMEN_TYPE_URL_MAP[specimenType];
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('specimen_type', urlValue);
        return newParams;
      },
      { replace: true },
    );
  };

  const handlePathwayClick = (pathway: OrderingPathway) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('ordering_pathway', ORDERING_PATHWAY_URL_MAP[pathway]);
        return newParams;
      },
      { replace: true },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      maxWidth='sm'
      fullWidth
      data-testid='new-order-type-selector-dialog'
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Ordering Pathway Section (CS/Sales users only) */}
        {isCSOrSalesUser && (
          <Box sx={{ px: 3, pt: 2, pb: enabledTypes.length > 1 ? 0 : 2 }}>
            <SectionTitle>Ordering Pathway</SectionTitle>
            {pathways.map((pathway) => (
              <OptionContainer
                key={pathway}
                onClick={() => handlePathwayClick(pathway)}
              >
                <StyledFormControlLabel
                  value={pathway}
                  control={
                    <Radio
                      checked={selectedPathway === pathway}
                      value={pathway}
                      disableRipple
                      sx={{ marginLeft: theme.spacing(-0.25), py: 0 }}
                    />
                  }
                  label={ORDERING_PATHWAY_DISPLAY_NAMES[pathway]}
                />
              </OptionContainer>
            ))}
          </Box>
        )}
        {/* Divider only shown if both sections are visible */}
        {isCSOrSalesUser && enabledTypes.length > 1 && (
          <Divider sx={{ my: 2 }} />
        )}

        {/* Order Type Section - only show if there are multiple options */}
        {enabledTypes.length > 1 && (
          <Box sx={{ px: 3, pb: 2 }}>
            <SectionTitle>Order Type</SectionTitle>
            {enabledTypes.map((specimenType) => (
              <OptionContainer
                key={specimenType.type}
                onClick={() => handleSpecimenTypeClick(specimenType.type)}
              >
                <StyledFormControlLabel
                  value={specimenType.type}
                  control={
                    <Radio
                      checked={selectedSpecimenType === specimenType.type}
                      value={specimenType.type}
                      disableRipple
                      sx={{ marginLeft: theme.spacing(-0.25), py: 0 }}
                    />
                  }
                  label={specimenType.displayName}
                />
                <OptionDescription>
                  {specimenType.description}
                </OptionDescription>
              </OptionContainer>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'end' }}>
        <Button size='large' onClick={onCancel}>
          CLOSE
        </Button>
        <Box
          sx={{
            width: theme.spacing(2),
          }}
        />
        <Button
          onClick={handleContinue}
          variant='contained'
          size='large'
          disabled={!selectedSpecimenType && enabledTypes.length > 1}
        >
          CONTINUE
        </Button>
      </DialogActions>
    </Dialog>
  );
};
